/**
 * useRelationshipsSource — dichiara che questo componente ha bisogno delle
 * relazioni di una sorgente, e garantisce che siano caricate.
 *
 * Sostituisce il pattern «la detail page dispatcha fetchRelationships al mount
 * e pulisce all'unmount», che aveva due difetti:
 *
 *  - **ordine di montaggio implicito.** I pannelli relazione non fetchavano mai:
 *    dipendevano dal fatto che la pagina ospite lo avesse fatto per loro. La
 *    timeline (`useEntityActivities`) idem. Funzionava per convenzione, non per
 *    costruzione, e un consumer montato altrove trovava lo store vuoto.
 *  - **doppio GET** appena due consumer della stessa sorgente provavano a
 *    fetchare per conto proprio.
 *
 * Qui la fetch parte solo se il bucket della sorgente e' `idle` — chi arriva
 * secondo trova `loading`/`succeeded` e non rifetcha. Il cleanup e' ref-contato:
 * il bucket viene svuotato solo quando smonta l'**ultimo** consumatore, cosi'
 * chiudere i pannelli non azzera le relazioni sotto la timeline che resta.
 *
 * NB: `className` e' il **FQN** (da `getEntityFqn`), non il typeName corto.
 * Passare `undefined` (tipo non registrato) e' un no-op: nessuna fetch.
 */

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchRelationships } from './relationshipThunks';
import {
  clearRelationships,
  relationshipSourceKey,
  selectRelationshipStatusBySource,
} from './relationshipSlice';

/**
 * Consumatori vivi per chiave di sorgente. Modulo-level e non nello store:
 * e' un dettaglio del ciclo di vita dei componenti, non stato applicativo — e
 * non deve provocare re-render quando cambia.
 */
const consumerCounts = new Map<string, number>();

export function useRelationshipsSource(
  className: string | undefined,
  classPK: number | undefined,
): void {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectRelationshipStatusBySource(className, classPK ?? 0));

  // Il fetch e' separato dal ref-count: dipende da `status`, che cambia nel
  // tempo, mentre il conteggio deve reggere esattamente un incremento per
  // montaggio. Fonderli rifarebbe +1/-1 a ogni transizione di stato.
  useEffect(() => {
    if (!className || classPK == null) return;
    if (status === 'idle') {
      dispatch(fetchRelationships({ className, classPK }));
    }
  }, [className, classPK, status, dispatch]);

  useEffect(() => {
    if (!className || classPK == null) return;
    const key = relationshipSourceKey(className, classPK);
    consumerCounts.set(key, (consumerCounts.get(key) ?? 0) + 1);
    return () => {
      const next = (consumerCounts.get(key) ?? 1) - 1;
      if (next > 0) {
        consumerCounts.set(key, next);
        return;
      }
      consumerCounts.delete(key);
      dispatch(clearRelationships({ className, classPK }));
    };
  }, [className, classPK, dispatch]);
}
